import React, { useEffect, useState } from 'react';
import { IMinDetails, IProductDelivery, IproductReferenceDataInCountry, IShipmentData } from '../../../../types/orders';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import ProductSelectorList from './components/ProductSelectorList/ProductSelectorList';
import ProductSelectorHeader from './components/ProductSelectorHeader/ProductSelectorHeader';
import { PRODUCT_BRANDS } from 'types/products';
import './ProductSelector.scss';

interface ProductSelectorProps {
  shippingChosenByDefault?: IShipmentData;
  listOfShipmentsAvailableToAdd?: IShipmentData[];
  setListOfShipmentsToChangeStatus?: React.Dispatch<React.SetStateAction<IShipmentData[]>>;
  errorList?: IMinDetails[];
  setErrorList?: React.Dispatch<React.SetStateAction<IMinDetails[] | undefined>>;
  hasTemporaryStorage?: boolean;
  customAlertMessage?: string | React.ReactNode;
  displayTemporaryStorageAlert?: boolean;
  serialHasAnyError: (hasErrors: boolean) => void;
  requireSerials?: boolean;
  headerTitle: string;
  references?: IproductReferenceDataInCountry[];
  isStockOrder?: boolean;
  listOfSelectedShipments?: IShipmentData[];
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  listOfSelectedShipments = [],
  listOfShipmentsAvailableToAdd,
  shippingChosenByDefault,
  setListOfShipmentsToChangeStatus,
  errorList,
  setErrorList,
  customAlertMessage,
  displayTemporaryStorageAlert,
  hasTemporaryStorage,
  serialHasAnyError,
  requireSerials,
  headerTitle,
  references,
  isStockOrder
}) => {
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [listOfSelectedItems, setListOfSelectedItems] = useState<IShipmentData[]>([]);
  const [listOfUnselectedItems, setListOfUnselectedItems] = useState<IShipmentData[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const displayDivider = listOfUnselectedItems?.length > 0;

  const selectedProducts =
    listOfSelectedItems?.flatMap(shipment =>
      shipment.products.filter(product => selectedProductIds.includes(product.id))
    ) || [];

  const addToListToChangeStatus = (shipmentId: number) => {
    const item = listOfUnselectedItems?.find(item => item?.shipmentId === shipmentId);
    const removeFromTheUnSelectedList = listOfUnselectedItems?.filter(item => item?.shipmentId !== shipmentId);
    if (item) setListOfSelectedItems([...listOfSelectedItems, item]);
    setListOfUnselectedItems([...removeFromTheUnSelectedList]);
  };

  const removeToListToChangeStatus = (shipmentId: number) => {
    const item = listOfSelectedItems?.find(item => item?.shipmentId === shipmentId);
    const removeFromTheSelectedList = listOfSelectedItems?.filter(item => item?.shipmentId !== shipmentId);

    if (item) {
      const productIdsToRemove = item.products.map(product => product.id);
      setSelectedProductIds(prev => prev.filter(productId => !productIdsToRemove.includes(productId)));
      setListOfUnselectedItems([...listOfUnselectedItems, item]);
    }

    setListOfSelectedItems([...removeFromTheSelectedList]);
  };

  const cleanSelectedProductIds = () => {
    setSelectAll(false);
    setSelectedProductIds([]);
    setListOfUnselectedItems([...listOfUnselectedItems, ...listOfSelectedItems]);
    setListOfSelectedItems([]);
  };

  const handleSelectAllChange = () => {
    if (selectAll) return cleanSelectedProductIds();
    setSelectAll(true);
    const allProductIds = [...listOfSelectedItems, ...listOfUnselectedItems].flatMap(shipment =>
      shipment.products.map(product => product.id)
    );
    setSelectedProductIds(allProductIds);
    setListOfSelectedItems([...listOfSelectedItems, ...listOfUnselectedItems]);
    setListOfUnselectedItems([]);
  };

  const defaultSelectItem = () => {
    if (!!shippingChosenByDefault && listOfSelectedShipments?.length === 0) {
      setListOfSelectedItems([shippingChosenByDefault]);
    } else {
      setListOfSelectedItems(listOfSelectedShipments || []);
    }
  };

  const selectItemsList = () => {
    if (!listOfShipmentsAvailableToAdd) return;
    const shipmentsWithValidMPN = listOfShipmentsAvailableToAdd?.filter(
      item => !item.products.find(product => product.brand === PRODUCT_BRANDS.APPLE && !product.mpn)
    ).filter(item => !listOfSelectedShipments?.some(selectedItem => selectedItem.shipmentId === item.shipmentId));
    setListOfUnselectedItems(shipmentsWithValidMPN);
  };

  const toggleProductSelection = (productId: number) => {
    if (isStockOrder) return;
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) return prev.filter(id => id !== productId);
      return [...prev, productId];
    });
  };

  const getFilteredShipments = () => {
    if (!selectedProductIds.length) return [];
    return listOfSelectedItems
      ?.filter(shipment => shipment.products.some(product => selectedProductIds.includes(product.id)))
      ?.map(shipment => ({
        ...shipment,
        products: shipment.products.filter(product => selectedProductIds.includes(product.id))
      }));
  };

  const hasStockReservation = (product: IProductDelivery) => {
    if (product?.stockReservation?.stockProduct?.serial) return true;
    const stockSerial = getStockSerial(product);
    return !!stockSerial;
  };

  const getProductsSerial = (shipment: IShipmentData) => {
    return shipment?.products?.map(product => ({
      ...product,
      serial: product?.serial ?? ''
    }));
  };

  const getUpdatedListOfItemsToChangeStatus = () => {
    const listOfItemsToChangeStatus = getFilteredShipments();

    return listOfItemsToChangeStatus?.map(shipment => ({
      ...shipment,
      products: getProductsSerial(shipment)
    }));
  };

  const handleChangeSelectedItems = () => {
    const updatedListOfItemsToChangeStatus = getUpdatedListOfItemsToChangeStatus();
    setListOfShipmentsToChangeStatus?.(updatedListOfItemsToChangeStatus);
  };

  const editProductSerial = (productId: number, newSerial: string) => {
    const updatedShipments = listOfSelectedItems.map(shipment => ({
      ...shipment,
      products: shipment?.products?.map(product =>
        product?.id === productId ? { ...product, serial: newSerial } : product
      )
    }));
    setErrorList?.(undefined);
    setListOfSelectedItems([...updatedShipments]);
  };

  const getStockSerial = (product: IProductDelivery) => {
    const stockSerial = references
      ?.find(ref => ref.sku === product.sku)
      ?.items?.find(item => item.saleId === product.saleId)?.stockReservation?.stockProduct?.serial;
    return stockSerial || '';
  };

  const getStockReservationSerial = (product: IProductDelivery) => {
    if (product?.stockReservation?.stockProduct?.serial) return product.stockReservation.stockProduct.serial;
    return getStockSerial(product);
  };

  const getProductsWithoutStockSerial = (allSelectedProducts: IProductDelivery[]) => {
    return allSelectedProducts.filter(product => {
      const stockSerial = getStockSerial(product);
      return !stockSerial;
    });
  };

  const getDuplicateSerialInDatabase = (serial: string, errorList: IMinDetails[]) => {
    const duplicateSerialInDatabase = errorList?.find(
      error => String(error?.serial).trim().toLowerCase() === serial.trim().toLowerCase()
    );
    return duplicateSerialInDatabase ? 'Este serial ya está asignado a una herramienta' : undefined;
  };

  const getDuplicateSerialsInInputs = (targetSerial: string) => {
    const productsWithoutStockSerial = getProductsWithoutStockSerial(selectedProducts);
    const serials = productsWithoutStockSerial?.map(product => product?.serial?.trim()).filter(Boolean);
    const serialCount = serials.filter(serial => serial === targetSerial).length;
    return serialCount > 1 ? 'El serial está repetido' : undefined;
  };

  const getDuplicateSerials = (serial: string, errorList: IMinDetails[]) => {
    const duplicateSerialInDatabase = getDuplicateSerialInDatabase(serial, errorList || []);
    const duplicateSerialsInInputs = getDuplicateSerialsInInputs(serial);
    return !!duplicateSerialInDatabase ? duplicateSerialInDatabase : duplicateSerialsInInputs;
  };

  const getErrorText = (itemData: IProductDelivery) => {
    if (isStockOrder && !requireSerials) return undefined;
    const serial = itemData?.serial?.trim();
    if (!serial) return undefined;
    const duplicateSerials = getDuplicateSerials(serial, errorList || []);
    return duplicateSerials;
  };

  const hasProductsWithoutSerials = () => {
    return selectedProducts?.some(product => {
      const hasProductSerial = !!product?.serial?.trim();
      const hasStocKSerial = !!getStockReservationSerial(product)?.trim();
      return !hasProductSerial && !hasStocKSerial;
    });
  };

  const hasAnyDuplicateSerials = () => {
    const productsWithoutStockSerial = getProductsWithoutStockSerial(selectedProducts);
    const serials = productsWithoutStockSerial?.map(product => product?.serial?.trim()).filter(Boolean);
    const serialSet = new Set(serials);
    return serials.length !== serialSet.size;
  };

  const getSerialErrors = () => {
    if (isStockOrder && !requireSerials) return false;
    const hasLocalDuplicates = hasAnyDuplicateSerials();
    const hasApiErrors = !!errorList?.length;
    const hasMissingSerials = requireSerials && hasProductsWithoutSerials();
    return !!hasLocalDuplicates || !!hasApiErrors || !!hasMissingSerials;
  };

  const selectAllProductsWhenStockOrder = () => {
    if (!(isStockOrder && listOfSelectedItems.length > 0)) return;
    const allProductIds = listOfSelectedItems.flatMap(shipment => shipment.products.map(product => product.id));
    setSelectedProductIds(allProductIds);
  };

  useEffect(() => {
    defaultSelectItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    selectItemsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleChangeSelectedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfSelectedItems]);

  useEffect(() => {
    handleChangeSelectedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductIds]);

  useEffect(() => {
    const serialErrors = getSerialErrors();
    serialHasAnyError(serialErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfSelectedItems, errorList, selectedProductIds]);

  useEffect(() => {
    selectAllProductsWhenStockOrder();
  }, [isStockOrder, listOfSelectedItems]);

  return (
    <div className="productSelectorContainer">
      <ProductSelectorHeader
        headerTitle={headerTitle}
        hasTemporaryStorage={hasTemporaryStorage}
        selectAll={selectAll}
        handleSelectAllChange={handleSelectAllChange}
        displayTemporaryStorageAlert={displayTemporaryStorageAlert}
      />

      <div className="bodyContainer">
        <div className="title">Productos Seleccionados</div>

        {listOfSelectedItems?.length > 0 && (
          <>
            <ProductSelectorList
              items={listOfSelectedItems}
              unselectedItems={listOfUnselectedItems}
              onToggleItem={removeToListToChangeStatus}
              showSerials
              editProductSerial={editProductSerial}
              getErrorText={getErrorText}
              isChecked
              toggleProductSelection={toggleProductSelection}
              selectedProductIds={selectedProductIds}
              hasStockReservation={hasStockReservation}
              getStockReservationSerial={getStockReservationSerial}
              isStockOrder={isStockOrder}
              requireSerials={requireSerials}
            />
            {customAlertMessage && (
              <div className="hasTemporaryStorageAlert">
                <div className="iconAlertContainer">
                  <BordOneToneIcon variant="alertTriangleWarning" customWidth="16px" />
                </div>
                <span className="alertMessageText">{customAlertMessage}</span>
              </div>
            )}
          </>
        )}

        {displayDivider && <div className="divider" />}

        {listOfUnselectedItems?.length > 0 && (
          <div className="selectedProducts">
            <div className="title">Otros Productos</div>
            <ProductSelectorList
              items={listOfUnselectedItems}
              unselectedItems={listOfSelectedItems}
              onToggleItem={addToListToChangeStatus}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSelector;
